import React, { useMemo, useState } from "react";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { LiveLTPCell } from "./OptimizedCell";
import BulkModifyModal from "./BulkModifyModal";
import { Pencil } from "lucide-react";
import { toast } from "react-hot-toast";
import { getStockFromSymbol } from "../hooks/useStockOptionsView";
import { getCommodityOptionFromSymbol } from "../hooks/useCommodityOptionsView";

/**
 * StockCommodityOptionsOrderComparisonModal
 * UI exactly matches OrderComparisonModal.
 * Pass category="stock" or category="commodity".
 */
export function StockCommodityOptionsOrderComparisonModal({
  isOpen,
  onClose,
  clientData,
  orders,
  symbolStrategyBaseQty,
  onCancel,
  category = "stock",
}) {
  const { indexMultipliers } = useGlobalData();

  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [modifyModalOpen, setModifyModalOpen] = useState(false);
  const [ordersToModify, setOrdersToModify] = useState([]);

  const isStock = category === "stock";
  const modalTitle = isStock
    ? "Stock Options – Order Comparison"
    : "Commodity Options – Order Comparison";

  function getSegmentName(symbol) {
    if (!symbol) return null;
    if (isStock) return getStockFromSymbol(symbol);
    return getCommodityOptionFromSymbol(symbol);
  }

  function extractStrike(symbol) {
    const match = symbol?.match(/(\d{3,6})(CE|PE)$/);
    return match ? match[1] : "N/A";
  }

  function formatPrice(num) {
    if (num === undefined || num === null || num === 0) return "-";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function formatOrderTime(dt) {
    if (!dt || dt === "N/A") return "-";
    try {
      return new Date(dt).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      });
    } catch (e) { return dt; }
  }

  function getMultiplier(segmentName) {
    if (!indexMultipliers || !clientData?.clientId) return 1;
    const matches = indexMultipliers.filter(
      (m) => m.client_id === clientData.clientId && m.index === segmentName
    );
    const mult = matches.find((m) =>
      ["dhan_xts", "dhan_xts_anurag"].includes((m.broker || "").toLowerCase())
    ) || matches[0];
    return mult && !isNaN(parseFloat(mult.multiplier)) ? parseFloat(mult.multiplier) : 1;
  }

  const ordersBySymbolStrategy = useMemo(() => {
    if (!orders || !clientData?.clientId) return [];

    const rows = [];
    const processedKeys = new Set();
    const clientId = (clientData.clientId || "").toString().trim();

    const clientOrders = orders.filter((o) => {
      const side = String(o.side || "").trim().toLowerCase();
      const cId = (o.clientId || o.client_id || "").toString().trim();
      return (side === "1" || side === "buy" || side === "b") &&
        cId === clientId &&
        getSegmentName(o.symbol) !== null;
    });

    clientOrders.forEach((order) => {
      const rowUniqueKey = `${order.symbol}_${order.strategy || "NO_STRATEGY"}_${order.order_id || order.orderId}`;
      const aggregationKey = `${order.symbol}_${order.strategy || "NO_STRATEGY"}_${order.stop_price || 0}_${order.limit_price || 0}`;
      const lookupKey = `${order.symbol}_${order.strategy || "NO_STRATEGY"}`;

      if (processedKeys.has(rowUniqueKey)) return;
      processedKeys.add(rowUniqueKey);

      let actualQty = 0;
      clientOrders.forEach((o) => {
        const oKey = `${o.symbol}_${o.strategy || "NO_STRATEGY"}_${o.stop_price || 0}_${o.limit_price || 0}`;
        if (oKey === aggregationKey) actualQty += Math.abs(parseFloat(o.qty || o.quantity || 0));
      });

      const baseQtyInfo = symbolStrategyBaseQty ? symbolStrategyBaseQty[lookupKey] : null;
      const orderQty = Math.abs(parseFloat(order.qty || order.quantity || 0));
      const baseQty = baseQtyInfo ? baseQtyInfo.baseQty : orderQty;

      const segmentName = getSegmentName(order.symbol) || (isStock ? "STOCK" : "COMMODITY");
      const rawMult = getMultiplier(segmentName.toUpperCase());
      const multiplier = (rawMult === "N/A" || typeof rawMult !== "number" || isNaN(rawMult)) ? 1 : rawMult;
      const expectedQty = multiplier * baseQty;

      rows.push({
        symbol: order.symbol,
        strategy: order.strategy || "N/A",
        index: segmentName,
        baseQty,
        multiplier,
        orderQty,
        expectedQty,
        actualQty,
        stop_price: order.stop_price,
        limit_price: order.limit_price,
        order_id: order.order_id || order.orderId || "N/A",
        orderdatetime: order.orderdatetime || order.orderDateTime || "N/A",
        match: actualQty === expectedQty,
        clientsTrading: baseQtyInfo ? baseQtyInfo.clients : 1,
        strike: extractStrike(order.symbol),
        status: order.status || "",
        originalOrder: order,
      });
    });

    return rows;
  }, [orders, clientData, symbolStrategyBaseQty, category]);

  const [selectedAsset, setSelectedAsset] = useState("ALL");

  const uniqueAssets = useMemo(() => {
    const assets = new Set(ordersBySymbolStrategy.map((r) => r.index).filter(Boolean));
    return ["ALL", ...Array.from(assets).sort()];
  }, [ordersBySymbolStrategy]);

  const filteredRows = useMemo(() => {
    if (selectedAsset === "ALL") return ordersBySymbolStrategy;
    return ordersBySymbolStrategy.filter((r) => r.index === selectedAsset);
  }, [ordersBySymbolStrategy, selectedAsset]);

  const toggleOrderSelection = (orderId) => {
    const next = new Set(selectedOrderIds);
    if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
    setSelectedOrderIds(next);
  };

  const handleBulkModify = () => {
    const toMod = filteredRows
      .filter((r) => selectedOrderIds.has(r.order_id))
      .map((r) => ({ ...r.originalOrder, expectedQty: r.expectedQty }));
    if (toMod.length === 0) { toast.error("Select orders to modify"); return; }
    setOrdersToModify(toMod);
    setModifyModalOpen(true);
  };

  const handleSingleModify = (row) => {
    setOrdersToModify([{ ...row.originalOrder, expectedQty: row.expectedQty }]);
    setModifyModalOpen(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-[#1a1d29] rounded-lg shadow-xl max-w-7xl w-full max-h-[80vh] overflow-hidden flex flex-col border border-gray-700/50" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-[#14161f] px-6 py-4 border-b border-gray-700 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-white">{modalTitle}</h2>
              {selectedOrderIds.size > 0 && (
                <button onClick={handleBulkModify} className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded transition-all shadow-lg flex items-center gap-2">
                  Modify Selected ({selectedOrderIds.size})
                </button>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors text-2xl font-bold leading-none">×</button>
          </div>
          {/* Client Info */}
          <div className="bg-[#1a1d29] px-6 py-3 border-b border-gray-700/50">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-400">Client:</span>
              <span className="text-white font-semibold">{clientData?.clientName || clientData?.clientId}</span>
              <span className="text-gray-400">({clientData?.clientId})</span>
            </div>
          </div>
          {/* Content */}
          <div className="flex-1 overflow-auto">
            {ordersBySymbolStrategy.length === 0 ? (
              <div className="text-center py-8 px-6">
                <p className="text-gray-400 mb-4">No orders found for comparison</p>
                <div className="text-left bg-red-900/20 border border-red-700/30 rounded p-4 max-w-2xl mx-auto text-xs">
                  <p className="text-red-400 font-semibold mb-2">Debug Info:</p>
                  <p className="text-gray-300">• Total orders available: {orders?.length || 0}</p>
                  <p className="text-gray-300">• Client ID: {clientData?.clientId || "N/A"}</p>
                  <p className="text-gray-300">• Client Name: {clientData?.clientName || "N/A"}</p>
                  <p className="text-gray-300">• Symbol+Strategy base qty entries: {Object.keys(symbolStrategyBaseQty || {}).length}</p>
                  <p className="text-yellow-400 mt-2">💡 Open browser console (F12) for detailed logs</p>
                </div>
              </div>
            ) : (
              <div className="bg-[#14161f] overflow-hidden">
                <div className="overflow-auto max-h-[calc(80vh-280px)]">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0f1117] sticky top-0 z-10">
                      <tr className="border-b border-gray-700/30">
                        <th className="w-8 px-2 text-center">
                          <input type="checkbox" className="w-4 h-4 rounded border-gray-700 bg-gray-900 text-blue-600 focus:ring-blue-500"
                            checked={filteredRows.length > 0 && filteredRows.every((r) => selectedOrderIds.has(r.order_id))}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedOrderIds(new Set(filteredRows.map((r) => r.order_id)));
                              else setSelectedOrderIds(new Set());
                            }} />
                        </th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Order ID</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Order Time</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">
                          <div className="flex items-center gap-2">
                            <span>{isStock ? "Stock" : "Commodity"}</span>
                            <select value={selectedAsset} onChange={(e) => setSelectedAsset(e.target.value)} onClick={(e) => e.stopPropagation()} className="bg-[#1a1d29] border border-gray-700 text-gray-300 text-xs rounded px-1 py-0.5 focus:outline-none focus:border-blue-500">
                              {uniqueAssets.map((asset) => (<option key={asset} value={asset}>{asset === "ALL" ? "All" : asset.toUpperCase()}</option>))}
                            </select>
                          </div>
                        </th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Symbol</th>
                        <th className="text-left py-3 px-4 text-gray-400 font-medium">Strategy</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Strike</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Stop Price</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium whitespace-nowrap">Limit Price</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium whitespace-nowrap">LTP</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Base Qty (1x)</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Multiplier</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Order Qty</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Expected Total</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Actual Total</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Match</th>
                        <th className="text-center py-3 px-4 text-gray-400 font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row, idx) => {
                        const isMatch = row.actualQty === row.expectedQty;
                        const qtyColor = isMatch ? "text-green-400" : "text-red-400";
                        return (
                          <tr key={idx} className="border-b border-gray-800/30 hover:bg-[#1a1d29] transition-colors">
                            <td className="px-2 text-center">
                              <input type="checkbox" className="w-3.5 h-3.5 rounded border-gray-800 bg-gray-950 text-blue-600 focus:ring-blue-500"
                                checked={selectedOrderIds.has(row.order_id)} onChange={() => toggleOrderSelection(row.order_id)} />
                            </td>
                            <td className="py-2.5 px-4 text-gray-300 text-xs font-mono">{row.order_id}</td>
                            <td className="py-2.5 px-4 text-gray-300 text-xs">{formatOrderTime(row.orderdatetime)}</td>
                            <td className="py-2.5 px-4"><span className="text-blue-400 text-xs font-semibold">{(row.index || "").toUpperCase()}</span></td>
                            <td className="py-2.5 px-4 text-gray-300 text-xs font-mono">{row.symbol}</td>
                            <td className="py-2.5 px-4 text-gray-400 text-xs">{row.strategy}</td>
                            <td className="py-2.5 px-4 text-center text-gray-300">{row.strike}</td>
                            <td className="py-2.5 px-4 text-center text-gray-300">{row.stop_price}</td>
                            <td className="py-2.5 px-4 text-center text-gray-300">{row.limit_price}</td>
                            <td className="py-2.5 px-4 text-center text-blue-400 font-medium"><LiveLTPCell symbol={row.symbol} formatFn={formatPrice} /></td>
                            <td className="py-2.5 px-4 text-center text-white font-medium">{row.baseQty}</td>
                            <td className="py-2.5 px-4 text-center text-purple-400 font-medium">{row.multiplier}x</td>
                            <td className="py-2.5 px-4 text-center text-gray-300 font-medium">{row.orderQty}</td>
                            <td className="py-2.5 px-4 text-center text-yellow-400 font-medium">{row.expectedQty}</td>
                            <td className={`py-2.5 px-4 text-center font-bold ${qtyColor}`}>{row.actualQty}</td>
                            <td className="py-2.5 px-4 text-center">{isMatch ? <span className="text-green-400">✓</span> : <span className="text-red-400">✗</span>}</td>
                            <td className="py-2.5 px-4 text-center">
                              {["pending", "open", "trigger pending", "not sent"].includes((row.status || "").toLowerCase()) && row.originalOrder ? (
                                <div className="flex items-center justify-center gap-1">
                                  <button onClick={(e) => { e.stopPropagation(); handleSingleModify(row); }} className="p-1 text-blue-400 hover:text-white hover:bg-blue-600/20 border border-blue-800/50 rounded transition-all" title="Modify Order">
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); if (onCancel) onCancel([row.originalOrder]); else if (typeof window.onCancelOrder === "function") window.onCancelOrder([row.originalOrder]); }} className="px-2 py-1 text-[10px] bg-red-900/30 hover:bg-red-800/40 text-red-200 border border-red-800/50 rounded transition-colors">
                                    Cancel
                                  </button>
                                </div>
                              ) : <span className="text-gray-500 text-[10px]">-</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <BulkModifyModal isOpen={modifyModalOpen} onClose={() => setModifyModalOpen(false)} orders={ordersToModify} onSuccess={() => setSelectedOrderIds(new Set())} />
    </>
  );
}
